import React, { useEffect, useMemo, useState } from "react";

const dateValue = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

export default function PublishToTradeModal({
  isOpen,
  onClose,
  file,
  module,
  projectId,
  onPublished,
}) {
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [drawingCompany, setDrawingCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const fileFormat = useMemo(() => {
    const name = file?.original_name || file?.name || "";
    return name.includes(".") ? name.split(".").pop().toUpperCase() : "";
  }, [file]);

  useEffect(() => {
    if (!isOpen || !file || !projectId) return;
    setDisplayName(file.display_name || file.original_name || file.name || "");
    setIssueDate(dateValue(file.issue_date));
    setDrawingCompany(file.author && file.author !== "NA" ? file.author : "");
    setCategoryId("");
    setError("");
    setSuccess("");
    setLoading(true);
    fetch(`/api/trade/projects/${projectId}/categories`)
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Unable to load Trade folders");
        return data;
      })
      .then((data) => {
        setCategories(Array.isArray(data) ? data : []);
        if (data?.length) setCategoryId(data[0]._id);
      })
      .catch((requestError) => {
        setCategories([]);
        setError(requestError.message);
      })
      .finally(() => setLoading(false));
  }, [isOpen, file, projectId]);

  if (!isOpen || !file) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    if (!["PDF", "DWG", "ZIP"].includes(fileFormat)) {
      setError("Only PDF, DWG, and ZIP files can be published to Trade.");
      return;
    }
    if (!categoryId || !displayName.trim() || !issueDate || !drawingCompany.trim()) {
      setError("Trade folder, display name, issue date, and drawing company are required.");
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch("/api/trade/publish", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          module,
          fileId: file._id,
          projectId,
          categoryId,
          displayName: displayName.trim(),
          issueDate,
          drawingCompany: drawingCompany.trim(),
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Publish failed");
      setSuccess("Published to Trade as Pending.");
      if (onPublished) onPublished(data);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-[560px] max-w-[94vw] max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Publish to Trade</h3>
          <button type="button" onClick={onClose} className="text-xl text-gray-500">×</button>
        </div>

        <div className="bg-gray-50 border rounded p-3 mb-4 text-xs space-y-1">
          <div><strong>Source module:</strong> {module}</div>
          <div><strong>Original file:</strong> {file.original_name || file.name}</div>
          <div><strong>Format:</strong> {fileFormat || "Unknown"}</div>
          <div><strong>Current display name:</strong> {file.display_name || "—"}</div>
          <div><strong>Current issue date:</strong> {dateValue(file.issue_date) || "—"}</div>
          <div className="text-gray-500 pt-1">The source file remains unchanged. S3 will copy it into independent Trade storage.</div>
        </div>

        {error && <div className="bg-red-100 text-red-700 px-3 py-2 rounded mb-3">{error}</div>}
        {success && <div className="bg-green-100 text-green-700 px-3 py-2 rounded mb-3">{success}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block font-medium mb-1">Target Trade folder</label>
            <select
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
              disabled={loading || submitting}
              className="w-full border rounded px-3 py-2"
              required
            >
              <option value="">{loading ? "Loading…" : "Select a Trade folder"}</option>
              {categories.map((category) => (
                <option key={category._id} value={category._id}>{category.display_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-medium mb-1">Display name</label>
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="w-full border rounded px-3 py-2" required />
          </div>
          <div>
            <label className="block font-medium mb-1">Issue date</label>
            <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className="w-full border rounded px-3 py-2" required />
          </div>
          <div>
            <label className="block font-medium mb-1">Drawing company</label>
            <input value={drawingCompany} onChange={(e) => setDrawingCompany(e.target.value)} className="w-full border rounded px-3 py-2" required />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="bg-gray-200 px-4 py-2 rounded">Close</button>
            <button
              type="submit"
              disabled={submitting || loading || Boolean(success)}
              className="text-white px-4 py-2 rounded disabled:opacity-50"
              style={{ backgroundColor: "#224228" }}
            >
              {submitting ? "Publishing…" : "Publish"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function PublishToTradeButton({ file, module, projectId, userRole }) {
  const [open, setOpen] = useState(false);
  if (!["admin", "office"].includes(userRole)) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-emerald-700 hover:underline"
        title="Publish an independent copy to the Trade portal"
      >
        Publish
      </button>
      <PublishToTradeModal
        isOpen={open}
        onClose={() => setOpen(false)}
        file={file}
        module={module}
        projectId={projectId}
      />
    </>
  );
}
